/**
 * Safe evaluator for server-supplied tax-form arithmetic formulas.
 *
 * Background
 * ----------
 * Legacy tax-form components (e.g. `form-credits-part-iii`, `partner-alloc`,
 * `f-6252`) receive `ATTRIB_CALC_FORMULA` strings from the backend and pass
 * them through `eval()` to compute line totals. The formula source is
 * server-trusted (loaded via authenticated `getFormData` API calls), but
 * `eval()` is still an XSS surface if the backend is ever compromised — it
 * runs in the full window scope and has access to `document`, `window`,
 * Angular DI tokens, etc.
 *
 * Approach
 * --------
 * 1. Whitelist-validate the formula string: only digits, decimal point,
 *    arithmetic operators (+ - * /), parentheses, commas, whitespace, and
 *    `Math.<name>` references are allowed.
 * 2. Evaluate via the `Function` constructor in strict mode. The Function
 *    constructor still has access to the global scope, but the whitelist
 *    above forbids identifiers other than `Math`, so the formula can only
 *    invoke `Math.*` methods.
 * 3. Any validation failure or runtime error returns the fallback (default
 *    `null`) and logs a `console.warn` with context so the bug is visible
 *    instead of silently swallowed (the previous behaviour).
 *
 * If we ever need to support a wider expression surface (variables, custom
 * functions), swap in a real expression parser (mathjs, expr-eval). Until
 * then this is intentionally minimal to keep the trust boundary tight.
 */

const ALLOWED_MATH_FUNCS = new Set([
  'abs', 'ceil', 'floor', 'max', 'min', 'pow', 'round',
  'sqrt', 'log', 'log2', 'log10', 'sign', 'trunc',
]);

const FORMULA_TOKEN = /Math\.[a-zA-Z_][a-zA-Z0-9_]*|[0-9]+(?:\.[0-9]+)?|[+\-*/(),\s]/g;

/**
 * Validate that the supplied string contains only arithmetic characters,
 * numbers, and approved `Math.*` calls. Returns true when the entire string
 * is consumed by the whitelist tokens.
 */
function isFormulaSafe(formula: string): boolean {
  if (typeof formula !== 'string' || formula.length === 0) {
    return false;
  }
  const stripped = formula.replace(FORMULA_TOKEN, '');
  if (stripped.length > 0) {
    return false;
  }
  const mathCalls = formula.match(/Math\.([a-zA-Z_][a-zA-Z0-9_]*)/g) || [];
  for (const call of mathCalls) {
    const fnName = call.slice(5);
    if (!ALLOWED_MATH_FUNCS.has(fnName)) {
      return false;
    }
  }
  return true;
}

/**
 * Evaluate an arithmetic formula string safely. Returns the numeric result,
 * or the fallback when validation fails or the expression throws.
 *
 * @param formula     The expression to evaluate. Expected to contain only
 *                    numbers, arithmetic operators, parentheses, and
 *                    whitelisted `Math.*` calls.
 * @param fallback    Returned when validation or evaluation fails.
 * @param contextLabel  Used in console warnings to identify the call site.
 */
export function safeEvalFormula(
  formula: string,
  fallback: number | null = null,
  contextLabel = 'tax-formula',
): number | null {
  if (!isFormulaSafe(formula)) {
    console.warn(
      `[safeEvalFormula] Rejected unsafe ${contextLabel} expression.`,
      { formula },
    );
    return fallback;
  }
  try {
    // The Function constructor is intentionally used here in place of
    // eval(). The whitelist above guarantees only arithmetic tokens and
    // Math.* identifiers appear, so the function body cannot reach the
    // surrounding closure or arbitrary globals.
    // eslint-disable-next-line no-new-func
    const fn = new Function(`"use strict"; return (${formula});`) as () => number;
    const result = fn();
    if (typeof result === 'number' && Number.isFinite(result)) {
      return result;
    }
    return fallback;
  } catch (err) {
    console.warn(
      `[safeEvalFormula] ${contextLabel} evaluation failed.`,
      { formula, error: err },
    );
    return fallback;
  }
}

/** True when safeEvalFormula returned a usable number (replaces parseInt(x) === parseInt(x)). */
export function isFiniteEvalNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
